import {
  DIP_BASELINE,
  GAME_VERSION,
  GameMode,
  RoundDescriptor,
  RULESET_ID,
  SCORE,
  createRoundDescriptor,
  isChallengingRound,
  scoreCounter,
  scoreForEnemy,
  shouldAwardBonus,
} from './ruleset';

export const SIMULATION_HZ = 60;
export const WORLD_WIDTH = 288;
export const WORLD_HEIGHT = 384;

export const InputBits = {
  NONE: 0,
  LEFT: 1,
  RIGHT: 2,
  FIRE: 4,
} as const;
export type InputBits = (typeof InputBits)[keyof typeof InputBits];

export type GamePhase = 'ready' | 'playing' | 'stage-clear' | 'paused' | 'game-over' | 'arcade-boundary';
export type EnemyPhase = 'entering' | 'formation' | 'attack' | 'captured' | 'destroyed';
export type CaptureState = 'none' | 'pulling' | 'enemy-controlled' | 'rescued' | 'returning';

export interface PlayerState {
  slot: 1 | 2;
  score: number;
  lives: number;
  fighterCount: 1 | 2;
  capture: CaptureState;
  turnsPlayed: number;
  morphs: number;
  captures: number;
  bonuses: number;
  nextBonusThreshold: number;
  gameOver: boolean;
}

export interface Shot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  owner: 'player' | 'enemy';
  dualIndex?: 0 | 1;
  charged?: boolean;
}

export interface EnemyEntity {
  id: number;
  kind: 'bee' | 'butterfly' | 'boss' | 'morph' | 'challenge';
  x: number;
  y: number;
  formationX: number;
  formationY: number;
  phase: EnemyPhase;
  hp: 1 | 2;
  damaged: boolean;
  active: boolean;
  escorts: number;
  morphKind?: 'scorpion' | 'bosconian' | 'flagship';
  challengeTarget?: 'dragonfly' | 'satellite' | 'starship';
  routeIndex?: number;
  attackTicks: number;
}

export interface Explosion {
  x: number;
  y: number;
  age: number;
  color: 'enemy' | 'fighter' | 'bonus';
}

export interface GameEvent {
  type:
    | 'fire'
    | 'enemy-hit'
    | 'boss-damaged'
    | 'enemy-destroyed'
    | 'tractor-beam'
    | 'capture'
    | 'rescue'
    | 'fighter-lost'
    | 'extra-life'
    | 'morph'
    | 'charged-cannon'
    | 'challenge-target'
    | 'challenge-perfect'
    | 'stage-clear'
    | 'stage-start'
    | 'turn-change'
    | 'game-over'
    | 'arcade-boundary';
  tick: number;
  slot: 1 | 2;
  points?: number;
  cue?: string;
}

export interface GameState {
  readonly rulesetId: typeof RULESET_ID;
  readonly gameVersion: typeof GAME_VERSION;
  readonly mode: GameMode;
  seed: number;
  tick: number;
  round: number;
  phase: GamePhase;
  activePlayer: 1 | 2;
  players: { 1: PlayerState; 2: PlayerState };
  playerX: number;
  shots: Shot[];
  enemyShots: Shot[];
  explosions: Explosion[];
  enemies: EnemyEntity[];
  descriptor: RoundDescriptor;
  stageTicks: number;
  formationReady: boolean;
  challengeDestroyed: number;
  challengeTotal: number;
  capturePullTicks: number;
  captiveAttackTicks: number;
  events: GameEvent[];
  randomState: number;
}

export interface ReplayEvent {
  sequence: number;
  tick: number;
  playerSlot: 1 | 2;
  inputBits: number;
  transition: 'none' | 'pause' | 'resume';
}

export interface ReplaySummary {
  score: number;
  stage: number;
  lives: number;
  turns: number;
  morphs: number;
  captures: number;
  bonuses: number;
  playerSlot: 1 | 2;
  transcriptHashInput: string;
  completed: boolean;
  arcadeBoundary: boolean;
  players: { 1: PlayerReplaySummary; 2: PlayerReplaySummary };
}

export interface PlayerReplaySummary {
  score: number;
  stage: number;
  lives: number;
  turns: number;
  morphs: number;
  captures: number;
  bonuses: number;
  playerSlot: 1 | 2;
}

function seeded(seed: number): number {
  const value = seed >>> 0;
  return value === 0 ? 0x6d2b79f5 : value;
}

function random(state: GameState): number {
  let x = state.randomState | 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  state.randomState = x >>> 0;
  return state.randomState / 0x1_0000_0000;
}

function player(slot: 1 | 2): PlayerState {
  return {
    slot,
    score: 0,
    lives: DIP_BASELINE.startingLives,
    fighterCount: 1,
    capture: 'none',
    turnsPlayed: 0,
    morphs: 0,
    captures: 0,
    bonuses: 0,
    nextBonusThreshold: DIP_BASELINE.bonusLifeFirst,
    gameOver: false,
  };
}

function formationX(column: number): number {
  return 48 + column * 27;
}

function formationY(row: number): number {
  return 48 + row * 25;
}

function createEnemies(descriptor: RoundDescriptor): EnemyEntity[] {
  if (descriptor.kind === 'challenging') {
    return descriptor.challengeRoutes.map((route, index) => ({
      id: index,
      kind: 'challenge',
      x: route.route[0]?.[0] ?? 0.5,
      y: route.route[0]?.[1] ?? 0.2,
      formationX: 0,
      formationY: 0,
      phase: 'attack',
      hp: 1,
      damaged: false,
      active: true,
      escorts: 0,
      challengeTarget: route.target,
      routeIndex: 0,
      attackTicks: 0,
    }));
  }
  return descriptor.roster.map((unit) => ({
    id: unit.id,
    kind: unit.kind,
    x: unit.entryWave % 2 === 0 ? -24 : WORLD_WIDTH + 24,
    y: -20 - unit.entryWave * 18,
    formationX: formationX(unit.column),
    formationY: formationY(unit.row),
    phase: 'entering',
    hp: unit.kind === 'boss' ? 2 : 1,
    damaged: false,
    active: unit.entryWave === 0,
    escorts: 0,
    attackTicks: 0,
  }));
}

export function createGameState(seed: number, mode: GameMode = 'single-player'): GameState {
  const descriptor = createRoundDescriptor(1);
  const state: GameState = {
    rulesetId: RULESET_ID,
    gameVersion: GAME_VERSION,
    mode,
    seed: seed >>> 0,
    tick: 0,
    round: 1,
    phase: 'playing',
    activePlayer: 1,
    players: { 1: player(1), 2: player(2) },
    playerX: WORLD_WIDTH / 2,
    shots: [],
    enemyShots: [],
    explosions: [],
    enemies: createEnemies(descriptor),
    descriptor,
    stageTicks: 0,
    formationReady: false,
    challengeDestroyed: 0,
    challengeTotal: descriptor.challengeRoutes.length,
    capturePullTicks: 0,
    captiveAttackTicks: 0,
    events: [],
    randomState: seeded(seed),
  };
  return state;
}

function activePlayer(state: GameState): PlayerState {
  return state.players[state.activePlayer];
}

function emit(state: GameState, type: GameEvent['type'], points?: number, cue?: string): void {
  state.events.push({ type, tick: state.tick, slot: state.activePlayer, ...(points === undefined ? {} : { points }), ...(cue === undefined ? {} : { cue }) });
}

export function addScore(state: GameState, points: number): void {
  const current = activePlayer(state);
  const before = current.score;
  current.score += Math.max(0, Math.floor(points));
  const earned = shouldAwardBonus(before, current.score);
  for (let index = 0; index < earned; index++) {
    current.lives++;
    current.bonuses++;
    emit(state, 'extra-life', undefined, 'extra-life');
  }
  if (current.score < DIP_BASELINE.bonusLifeFirst) current.nextBonusThreshold = DIP_BASELINE.bonusLifeFirst;
  else {
    current.nextBonusThreshold = DIP_BASELINE.bonusLifeFirst + (DIP_BASELINE.bonusLifeEvery - DIP_BASELINE.bonusLifeFirst);
    while (current.nextBonusThreshold <= current.score) current.nextBonusThreshold += DIP_BASELINE.bonusLifeEvery;
  }
}

export function playerDisplayScore(state: GameState, slot: 1 | 2): number {
  return scoreCounter(state.players[slot].score, slot);
}

export function destroyEnemy(state: GameState, enemyId: number, context: 'formation' | 'flight' = 'flight'): boolean {
  const enemy = state.enemies.find((candidate) => candidate.id === enemyId);
  if (!enemy || enemy.phase === 'destroyed') return false;
  if (enemy.kind === 'boss' && enemy.hp === 2) {
    enemy.hp = 1;
    enemy.damaged = true;
    emit(state, 'boss-damaged', undefined, 'boss-damage');
    return false;
  }
  const captorWasAttacking = enemy.kind === 'boss' && enemy.phase === 'attack';
  enemy.phase = 'destroyed';
  enemy.active = false;
  state.explosions.push({ x: enemy.x, y: enemy.y, age: 0, color: enemy.kind === 'challenge' ? 'bonus' : 'enemy' });
  let points = 0;
  if (enemy.kind === 'challenge') {
    points = SCORE.challengeTarget;
    state.challengeDestroyed++;
    emit(state, 'challenge-target', points, 'enemy-hit');
  } else if (enemy.kind === 'morph') {
    points = SCORE.morphTarget;
    emit(state, 'enemy-destroyed', points, 'enemy-destroyed');
  } else {
    points = scoreForEnemy(enemy.kind, context, enemy.escorts);
    emit(state, 'enemy-destroyed', points, 'enemy-destroyed');
  }
  addScore(state, points);
  if (enemy.kind === 'morph' && enemy.morphKind) {
    const group = state.enemies.filter((candidate) => candidate.kind === 'morph' && candidate.morphKind === enemy.morphKind);
    if (group.length > 0 && group.every((candidate) => candidate.phase === 'destroyed')) {
      const bonus = SCORE.morphBonus[enemy.morphKind];
      addScore(state, bonus);
      emit(state, 'morph', bonus, 'morph');
    }
  }
  const captured = activePlayer(state).capture === 'pulling' || activePlayer(state).capture === 'enemy-controlled';
  if (enemy.kind === 'boss' && captured && captorWasAttacking) rescueCapturedFighter(state);
  if (enemy.kind === 'boss' && captured && !captorWasAttacking) {
    activePlayer(state).capture = 'returning';
    state.captiveAttackTicks = 60;
  }
  return true;
}

export function damageEnemy(state: GameState, enemyId: number, context: 'formation' | 'flight' = 'flight'): boolean {
  const enemy = state.enemies.find((candidate) => candidate.id === enemyId);
  if (!enemy || enemy.phase === 'destroyed') return false;
  emit(state, 'enemy-hit', undefined, 'enemy-hit');
  return destroyEnemy(state, enemyId, context);
}

export function startTractorBeam(state: GameState, enemyId: number): boolean {
  const current = activePlayer(state);
  const boss = state.enemies.find((enemy) => enemy.id === enemyId && enemy.kind === 'boss');
  if (!boss || boss.phase !== 'attack' || current.capture !== 'none' || current.lives <= 0) return false;
  current.lives--;
  current.capture = 'pulling';
  current.captures++;
  state.capturePullTicks = 0;
  boss.attackTicks = 0;
  emit(state, 'tractor-beam', undefined, 'tractor-beam');
  return true;
}

function rescueCapturedFighter(state: GameState): void {
  const current = activePlayer(state);
  if (current.capture !== 'pulling' && current.capture !== 'enemy-controlled') return;
  current.capture = 'none';
  current.fighterCount = 2;
  emit(state, 'rescue', undefined, 'rescue');
}

export function loseFighter(state: GameState): void {
  const current = activePlayer(state);
  if (current.fighterCount === 2) {
    current.fighterCount = 1;
    emit(state, 'fighter-lost', undefined, 'fighter-lost');
    return;
  }
  current.lives = Math.max(0, current.lives - 1);
  current.capture = 'none';
  emit(state, 'fighter-lost', undefined, 'fighter-lost');
  if (current.lives > 0) return;
  current.gameOver = true;
  if (state.mode === 'alternating-two-player') {
    const otherSlot: 1 | 2 = state.activePlayer === 1 ? 2 : 1;
    const other = state.players[otherSlot];
    if (!other.gameOver && other.lives > 0) {
      state.activePlayer = otherSlot;
      other.turnsPlayed++;
      state.playerX = WORLD_WIDTH / 2;
      emit(state, 'turn-change', undefined, 'turn-change');
      return;
    }
  }
  state.phase = 'game-over';
  emit(state, 'game-over', undefined, 'game-over');
}

function fire(state: GameState): void {
  if (state.shots.length >= 2 || state.phase !== 'playing') return;
  const dual = activePlayer(state).fighterCount === 2;
  const positions = dual ? [state.playerX - 7, state.playerX + 7] : [state.playerX];
  for (const x of positions) {
    if (state.shots.length >= 2) break;
    state.shots.push({ x, y: WORLD_HEIGHT - 34, vx: 0, vy: -7.5, owner: 'player', dualIndex: dual ? (x < state.playerX ? 0 : 1) : undefined });
  }
  emit(state, 'fire', undefined, 'fire');
}

function updateShots(state: GameState): void {
  for (const shot of state.shots) shot.y += shot.vy;
  state.shots = state.shots.filter((shot) => shot.y > -12);
  for (const shot of state.enemyShots) {
    shot.x += shot.vx;
    shot.y += shot.vy;
  }
  state.enemyShots = state.enemyShots.filter((shot) => shot.y < WORLD_HEIGHT + 12 && shot.y > -12 && shot.x > -12 && shot.x < WORLD_WIDTH + 12);
}

function updateExplosions(state: GameState): void {
  for (const explosion of state.explosions) explosion.age++;
  state.explosions = state.explosions.filter((explosion) => explosion.age < 18);
}

function updateEntering(state: GameState): void {
  let allReady = true;
  for (const enemy of state.enemies) {
    if (enemy.phase !== 'entering') continue;
    if (enemy.active) {
      const dx = enemy.formationX - enemy.x;
      const dy = enemy.formationY - enemy.y;
      enemy.x += Math.sign(dx) * Math.min(Math.abs(dx), 2.2);
      enemy.y += Math.sign(dy) * Math.min(Math.abs(dy), 2.2);
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) enemy.phase = 'formation';
    } else allReady = false;
  }
  for (const enemy of state.enemies) {
    const entryWave = state.descriptor.roster.find((unit) => unit.id === enemy.id)?.entryWave ?? 0;
    if (enemy.phase === 'entering' && !enemy.active && state.tick >= entryWave * 72) enemy.active = true;
  }
  state.formationReady = allReady && state.enemies.every((enemy) => enemy.phase === 'formation' || enemy.phase === 'destroyed');
}

function updateFormationAttacks(state: GameState): void {
  if (state.descriptor.kind === 'challenging') return;
  const hasActiveAttack = state.enemies.some((enemy) => enemy.phase === 'attack');
  if (!state.formationReady && !hasActiveAttack) return;
  const candidates = state.enemies.filter((enemy) => enemy.phase === 'formation' && enemy.kind !== 'morph');
  if (state.formationReady && candidates.length > 0 && state.tick % state.descriptor.attackFrequencyTicks === 0) {
    const attackCount = state.descriptor.attackPattern === 'convoy' || state.descriptor.attackPattern === 'scissors' ? 2 : 1;
    const firstCandidate = Math.floor(random(state) * candidates.length);
    for (let offset = 0; offset < Math.min(attackCount, candidates.length); offset++) {
      const candidate = candidates[(firstCandidate + offset) % candidates.length]!;
      candidate.phase = 'attack';
      candidate.attackTicks = 0;
      candidate.escorts = state.descriptor.attackPattern === 'convoy' ? 2 : state.descriptor.attackPattern === 'scissors' ? 1 : 0;
      candidate.x = candidate.formationX;
      candidate.y = candidate.formationY;
    }
  }
  for (const enemy of state.enemies) {
    if (enemy.phase !== 'attack') continue;
    enemy.attackTicks++;
    const direction = enemy.id % 2 === 0 ? 1 : -1;
    const patternOffset = state.descriptor.attackPattern === 'sweep'
      ? direction * 1.4
      : state.descriptor.attackPattern === 'scissors'
        ? Math.sin(enemy.attackTicks / 8) * direction * 2
        : state.descriptor.attackPattern === 'spiral'
          ? Math.cos(enemy.attackTicks / 12) * 1.8
          : state.descriptor.attackPattern === 'convoy'
            ? direction * 0.8 + Math.sin(enemy.attackTicks / 16)
            : Math.sin(enemy.attackTicks / 18) * 1.2;
    enemy.x += patternOffset;
    enemy.y += state.descriptor.diveSpeed * 0.7;
    if (enemy.kind === 'boss' && enemy.attackTicks === 90 && activePlayer(state).capture === 'none') startTractorBeam(state, enemy.id);
    if (enemy.kind !== 'boss' && enemy.attackTicks % Math.max(36, 100 - Math.floor(state.descriptor.aggression * 40)) === 0) {
      state.enemyShots.push({ x: enemy.x, y: enemy.y, vx: (state.playerX - enemy.x) / 100, vy: 3.2 + state.descriptor.aggression, owner: 'enemy' });
    }
    if (enemy.y > WORLD_HEIGHT + 20) {
      enemy.phase = 'formation';
      enemy.x = enemy.formationX;
      enemy.y = enemy.formationY;
    }
  }
  if (state.descriptor.attackPattern === 'cannon' && state.tick % state.descriptor.cannonIntervalTicks === 0) {
    const cannon = state.enemies.find((enemy) => enemy.phase === 'formation' && enemy.kind === 'boss');
    if (cannon) {
      state.enemyShots.push({ x: cannon.x, y: cannon.y, vx: (state.playerX - cannon.x) / 120, vy: 2.2, owner: 'enemy', charged: true });
      emit(state, 'charged-cannon', undefined, 'charged-cannon');
    }
  }
}

function updateChallenge(state: GameState): void {
  if (state.descriptor.kind !== 'challenging') return;
  for (const enemy of state.enemies) {
    if (enemy.phase === 'destroyed') continue;
    const route = state.descriptor.challengeRoutes[enemy.id];
    if (!route) continue;
    enemy.attackTicks++;
    const routeIndex = Math.min(route.route.length - 1, Math.floor(enemy.attackTicks / 24));
    enemy.routeIndex = routeIndex;
    const target = route.route[routeIndex];
    if (target) {
      enemy.x = target[0] * WORLD_WIDTH;
      enemy.y = target[1] * WORLD_HEIGHT;
    }
    if (enemy.attackTicks > route.route.length * 24 + 30) enemy.phase = 'destroyed';
  }
  if (state.challengeDestroyed === state.challengeTotal && state.challengeTotal > 0) {
    addScore(state, SCORE.challengePerfect);
    emit(state, 'challenge-perfect', SCORE.challengePerfect, 'challenge-perfect');
    state.challengeDestroyed = -1;
  }
}

function updateCapture(state: GameState): void {
  const current = activePlayer(state);
  if (current.capture === 'returning') {
    state.captiveAttackTicks = Math.max(0, state.captiveAttackTicks - 1);
    return;
  }
  if (current.capture !== 'pulling') return;
  state.capturePullTicks++;
  if (state.capturePullTicks === 1) emit(state, 'capture', undefined, 'capture');
  if (state.capturePullTicks >= 72) current.capture = 'enemy-controlled';
}

function collideShots(state: GameState): void {
  for (const shot of state.shots) {
    let hit = false;
    for (const enemy of state.enemies) {
      if (!enemy.active || enemy.phase === 'destroyed') continue;
      const dx = shot.x - enemy.x;
      const dy = shot.y - enemy.y;
      if (dx * dx + dy * dy > 14 * 14) continue;
      damageEnemy(state, enemy.id, enemy.phase === 'formation' ? 'formation' : 'flight');
      shot.y = -100;
      hit = true;
      break;
    }
    if (hit) continue;
  }
  state.shots = state.shots.filter((shot) => shot.y > -50);
  for (const shot of state.enemyShots) {
    const dx = shot.x - state.playerX;
    const dy = shot.y - (WORLD_HEIGHT - 26);
    const hitRadius = activePlayer(state).fighterCount === 2 ? 18 : 14;
    if (dx * dx + dy * dy < hitRadius * hitRadius) {
      shot.y = WORLD_HEIGHT + 100;
      loseFighter(state);
    }
  }
  state.enemyShots = state.enemyShots.filter((shot) => shot.y < WORLD_HEIGHT + 50);
}

function maybeSpawnMorph(state: GameState): void {
  const morph = state.descriptor.morphs[0];
  if (!morph || state.tick !== morph.triggerWave * 180) return;
  const source = state.enemies.find((enemy) => enemy.kind === morph.source && enemy.phase === 'formation');
  if (!source) return;
  source.phase = 'destroyed';
  for (let index = 0; index < 3; index++) {
    state.enemies.push({
      id: 1000 + index + state.round * 3,
      kind: 'morph',
      morphKind: morph.kind,
      x: source.x + (index - 1) * 16,
      y: source.y,
      formationX: source.formationX + (index - 1) * 16,
      formationY: source.formationY,
      phase: 'attack',
      hp: 1,
      damaged: false,
      active: true,
      escorts: 0,
      attackTicks: 0,
    });
  }
  activePlayer(state).morphs++;
  emit(state, 'morph', undefined, 'morph');
}

function advanceRound(state: GameState): void {
  if (state.round === 255) {
    state.phase = 'arcade-boundary';
    emit(state, 'arcade-boundary', undefined, 'stage-clear');
    return;
  }
  state.round++;
  state.descriptor = createRoundDescriptor(state.round);
  state.enemies = createEnemies(state.descriptor);
  state.stageTicks = 0;
  state.formationReady = false;
  state.challengeDestroyed = 0;
  state.challengeTotal = state.descriptor.challengeRoutes.length;
  state.shots = [];
  state.enemyShots = [];
  const current = activePlayer(state);
  if (current.capture === 'returning' && state.descriptor.kind === 'normal') current.capture = 'none';
  state.captiveAttackTicks = 0;
  state.phase = 'playing';
  emit(state, 'stage-start', undefined, state.descriptor.kind === 'challenging' ? 'challenging-stage' : 'stage-start');
}

function maybeFinishStage(state: GameState): void {
  if (state.descriptor.kind === 'challenging') {
    if (state.enemies.every((enemy) => enemy.phase === 'destroyed')) {
      state.phase = 'stage-clear';
      state.stageTicks = 0;
      emit(state, 'stage-clear', undefined, 'stage-clear');
    }
    return;
  }
  if (state.formationReady && state.enemies.every((enemy) => enemy.phase === 'destroyed')) {
    state.phase = 'stage-clear';
    state.stageTicks = 0;
    emit(state, 'stage-clear', undefined, 'stage-clear');
  }
}

export function step(state: GameState, inputBits: number): GameState {
  if (state.phase === 'game-over' || state.phase === 'arcade-boundary') return state;
  state.events = [];
  state.tick++;
  if (state.phase === 'stage-clear') {
    state.stageTicks++;
    if (state.stageTicks >= 150) advanceRound(state);
    return state;
  }
  if (state.phase === 'paused') return state;
  const legalBits = inputBits & (InputBits.LEFT | InputBits.RIGHT | InputBits.FIRE);
  if ((legalBits & InputBits.LEFT) !== 0 && (legalBits & InputBits.RIGHT) === 0) state.playerX = Math.max(18, state.playerX - 3.2);
  if ((legalBits & InputBits.RIGHT) !== 0 && (legalBits & InputBits.LEFT) === 0) state.playerX = Math.min(WORLD_WIDTH - 18, state.playerX + 3.2);
  if ((legalBits & InputBits.FIRE) !== 0) fire(state);
  updateEntering(state);
  updateFormationAttacks(state);
  updateChallenge(state);
  updateCapture(state);
  maybeSpawnMorph(state);
  updateShots(state);
  updateExplosions(state);
  collideShots(state);
  maybeFinishStage(state);
  random(state);
  return state;
}

export function setPaused(state: GameState, paused: boolean): void {
  if (state.phase === 'game-over' || state.phase === 'arcade-boundary') return;
  state.phase = paused ? 'paused' : 'playing';
}

export function isLeaderboardEligible(state: GameState | null, serverChallengeAccepted: boolean): boolean {
  return Boolean(state && serverChallengeAccepted && state.phase === 'game-over');
}

export function replayTranscript(seed: number, mode: GameMode, events: readonly ReplayEvent[]): ReplaySummary {
  const state = createGameState(seed, mode);
  const byPlayer = new Map<1 | 2, ReplayEvent[]>();
  for (const event of events) {
    const list = byPlayer.get(event.playerSlot) ?? [];
    list.push(event);
    byPlayer.set(event.playerSlot, list);
  }
  const ordered = [...events].sort((left, right) => left.tick - right.tick || left.sequence - right.sequence);
  let cursor = 0;
  for (const event of ordered) {
    if (event.tick < cursor) continue;
    while (cursor < event.tick) {
      step(state, InputBits.NONE);
      cursor++;
    }
    if (event.transition === 'pause') setPaused(state, true);
    else if (event.transition === 'resume') setPaused(state, false);
    else step(state, event.inputBits);
  }
  const slot = state.activePlayer;
  const current = state.players[slot];
  return {
    score: current.score,
    stage: state.round,
    lives: current.lives,
    turns: current.turnsPlayed,
    morphs: current.morphs,
    captures: current.captures,
    bonuses: current.bonuses,
    playerSlot: slot,
    transcriptHashInput: JSON.stringify({ seed, mode, events: ordered }),
    completed: state.phase === 'game-over',
    arcadeBoundary: state.phase === 'arcade-boundary',
    players: {
      1: { score: state.players[1].score, stage: state.round, lives: state.players[1].lives, turns: state.players[1].turnsPlayed, morphs: state.players[1].morphs, captures: state.players[1].captures, bonuses: state.players[1].bonuses, playerSlot: 1 },
      2: { score: state.players[2].score, stage: state.round, lives: state.players[2].lives, turns: state.players[2].turnsPlayed, morphs: state.players[2].morphs, captures: state.players[2].captures, bonuses: state.players[2].bonuses, playerSlot: 2 },
    },
  };
}
